import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useBlockedProductStorageLocationOptions } from 'hooks/api/options';
import type { BlockedProductListEntry } from 'contracts/blocked-products';
import type { CreateRedirectedProductsPayload } from 'contracts/redirected-products';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs, TextareaGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components/activityIndicators';
import { ProductsTable } from 'pages/stock-inspection/shared-components';

import { formSchema, type FormSchema } from './form-schema';

export type CreateRedirectedProductsDrawerRef = {
  open: (products: BlockedProductListEntry[]) => void;
  close: () => void;
}

type Props = {
  onSubmit: (data: CreateRedirectedProductsPayload) => void;
  loading?: boolean;
}

export const CreateRedirectedProductsDrawer
  = React.forwardRef<CreateRedirectedProductsDrawerRef, Props>(({
                                                                  onSubmit,
                                                                  loading,
                                                                }, ref) => {
  const [products, setProducts] = React.useState<BlockedProductListEntry[]>([]);
  const [open, setOpen] = React.useState(false);
  const storageLocationOptions = useBlockedProductStorageLocationOptions();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    onSubmit({
      storageLocation: formData.storageLocation,
      productIds: products.map(p => p.id),
      comment: formData.comment ?? null,
    });
  }, [onSubmit, products]);

  const onOpenChange = React.useCallback((open: boolean) => {
    setOpen(open);
    form.reset();
  }, [form]);

  React.useImperativeHandle(ref, () => ({
    open: (selected) => {
      onOpenChange(selected.length > 0);
      setProducts(selected);
    },
    close: () => {
      onOpenChange(false);
      setProducts([]);
    },
  }), [onOpenChange]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Content>
        <div className="max-w-[800px] w-full mx-auto">
          <Drawer.Header>
            <Drawer.Title className="text-center flex flex-col font-GilroyBold text-gray-600 text-[16px]">
              <span>Produtos selecionados</span>
              <span className={'font-extrabold text-gray-800 text-[40px]'}>
                {products.length.toString().padStart(3, '0')}
              </span>
            </Drawer.Title>
          </Drawer.Header>
          <ProductsTable products={products} className={'max-h-[260px] overflow-y-auto mb-6'} />
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onFormSubmit)}
              className="flex flex-col gap-4 pb-10"
            >
              <FormField
                control={form.control}
                name={'storageLocation'}
                render={({ field }) => (
                  <ComboboxGs
                    form={form}
                    fieldName={field.name}
                    fieldValue={field.value}
                    content={storageLocationOptions.singleSelectOptions}
                    label={'SLOC'}
                    clearable
                    portal={false}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'comment'}
                render={({ field }) => (
                  <TextareaGs
                    {...field}
                    label={'Observação'}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <div className={'grid grid-cols-2 gap-4'}>
                <Button.Root
                  type={'button'}
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button.Root>
                <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                  {loading ? <ActivityIndicator /> : 'Salvar'}
                </Button.Root>
              </div>
            </form>
          </Form>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
});
CreateRedirectedProductsDrawer.displayName = 'CreateRedirectedProductsDrawer';